import React, { createContext, useContext, useReducer, useEffect } from "react";
import { User } from "@/types";
import { authAPI, getApiErrorPayload } from "@/lib/api";
import { authStorage } from "@/lib/auth-storage";

export type LoginCompanyChoice = { id: number; name: string | null };

/** Thrown by `login` when the mobile and password match accounts in several companies. */
export class SelectCompanyError extends Error {
	constructor(readonly companies: LoginCompanyChoice[]) {
		super("auth.errors.selectCompany");
	}
}

/** Duck-typed so the check survives transpiled Error subclasses. */
export const isSelectCompanyError = (error: unknown): error is SelectCompanyError =>
	error instanceof Error && Array.isArray((error as Partial<SelectCompanyError>).companies);

interface AuthState {
	user: User | null;
	token: string | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

type AuthAction =
	| { type: "LOGIN_START" }
	| { type: "LOGIN_SUCCESS"; payload: { user: User; token: string } }
	| { type: "LOGIN_FAILURE" }
	| { type: "LOGOUT" }
	| { type: "UPDATE_USER"; payload: User };

const initialState: AuthState = {
	user: null,
	token: authStorage.getAccessToken(),
	isAuthenticated: false,
	isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
	switch (action.type) {
		case "LOGIN_START":
			return {
				...state,
				isLoading: true,
			};
		case "LOGIN_SUCCESS":
			return {
				...state,
				user: action.payload.user,
				token: action.payload.token,
				isAuthenticated: true,
				isLoading: false,
			};
		case "LOGIN_FAILURE":
			return {
				...state,
				user: null,
				token: null,
				isAuthenticated: false,
				isLoading: false,
			};
		case "LOGOUT":
			return {
				...state,
				user: null,
				token: null,
				isAuthenticated: false,
				isLoading: false,
			};
		case "UPDATE_USER":
			return {
				...state,
				user: action.payload,
			};
		default:
			return state;
	}
};

interface AuthContextType extends AuthState {
	/** `remember` (the login page's "Remember me") keeps the session after the browser is closed. */
	/** `companyId` is sent only after the server answered `auth.errors.selectCompany`. */
	login: (phone: string, password: string, remember?: boolean, companyId?: number) => Promise<User>;
	logout: () => void;
	updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

interface AuthProviderProps {
	children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [state, dispatch] = useReducer(authReducer, initialState);

	useEffect(() => {
		// Check if user is already authenticated
		const token = authStorage.getAccessToken();
		const refreshToken = authStorage.getRefreshToken();
		if (token || refreshToken) {
			// Validate token and get user info
			validateToken(token ?? "");
		} else {
			dispatch({ type: "LOGIN_FAILURE" });
		}
	}, []);

	const validateToken = async (token: string) => {
		try {
			const user = await authAPI.profile();
			dispatch({
				type: "LOGIN_SUCCESS",
				payload: { user, token: authStorage.getAccessToken() ?? token },
			});
		} catch (error) {
			authStorage.clear();
			dispatch({ type: "LOGOUT" });
		}
	};

	const login = async (phone: string, password: string, remember = true, companyId?: number): Promise<User> => {
		dispatch({ type: "LOGIN_START" });

		try {
			const loginBody: Parameters<typeof authAPI.login>[0] = {
				phone: phone.trim(),
				password,
			};
			const tenantCid = process.env.REACT_APP_AUTH_COMPANY_ID;
			if (companyId != null) {
				loginBody.companyId = companyId;
			} else if (tenantCid && /^\d+$/.test(tenantCid)) {
				loginBody.companyId = parseInt(tenantCid, 10);
			}
			const menuSlug = process.env.REACT_APP_AUTH_MENU_SLUG?.trim();
			if (menuSlug) {
				loginBody.menuSlug = menuSlug;
			}
			const data = await authAPI.login(loginBody);
			authStorage.setTokens(data.access_token, data.refresh_token, remember);
			const user = data.user as User;
			dispatch({
				type: "LOGIN_SUCCESS",
				payload: { user, token: data.access_token },
			});
			return user;
		} catch (error) {
			authStorage.clear();
			dispatch({ type: "LOGIN_FAILURE" });
			const companies = (error as { response?: { data?: { companies?: unknown } } })?.response?.data?.companies;
			if (Array.isArray(companies)) {
				throw new SelectCompanyError(companies as LoginCompanyChoice[]);
			}
			throw new Error(getApiErrorPayload(error).key);
		}
	};

	const logout = () => {
		const accessToken = authStorage.getAccessToken();
		authStorage.clear();
		dispatch({ type: "LOGOUT" });
		void authAPI.logout(accessToken).catch(() => undefined);
	};

	const updateUser = (user: User) => {
		dispatch({ type: "UPDATE_USER", payload: user });
	};

	const value: AuthContextType = {
		...state,
		login,
		logout,
		updateUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
